import { useState, useEffect, useMemo } from "react";
import { api, BACKEND_URL } from "@/lib/rn3";
import { toast } from "sonner";
import {
  FileDown, FileSpreadsheet, FileJson, Calendar, ChevronDown, ChevronRight,
  TrendingUp, Scale, ArrowDownUp, BookOpen, Loader2, X,
} from "lucide-react";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayMonth = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const firstDayYear = () => {
  const d = new Date(); d.setMonth(0); d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const REPORTS = [
  { key: "pl",  icon: TrendingUp,   en: "Profit & Loss",   es: "Estado de Resultados" },
  { key: "bs",  icon: Scale,        en: "Balance Sheet",   es: "Balance General" },
  { key: "cf",  icon: ArrowDownUp,  en: "Cash Flow",       es: "Flujo de Efectivo" },
  { key: "gl",  icon: BookOpen,     en: "General Ledger",  es: "Libro Mayor" },
];

const PRESETS = [
  { key: "mtd", en: "MTD", es: "Mes Actual" },
  { key: "qtd", en: "QTD", es: "Trimestre" },
  { key: "ytd", en: "YTD", es: "Año a la Fecha" },
  { key: "last_month", en: "Last Month", es: "Mes Pasado" },
  { key: "custom", en: "Custom", es: "Personalizado" },
];

function presetRange(key) {
  const today = new Date();
  const start = new Date();
  const end = today.toISOString().slice(0, 10);
  if (key === "mtd") { start.setDate(1); }
  else if (key === "qtd") {
    const m = today.getMonth();
    start.setMonth(m - (m % 3));
    start.setDate(1);
  }
  else if (key === "ytd") { start.setMonth(0); start.setDate(1); }
  else if (key === "last_month") {
    start.setMonth(today.getMonth() - 1); start.setDate(1);
    const endD = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: start.toISOString().slice(0, 10), end: endD.toISOString().slice(0, 10) };
  }
  return { start: start.toISOString().slice(0, 10), end };
}

/* Drill-down modal */
const DrillModal = ({ open, onClose, account, lines, lang }) => {
  if (!open) return null;
  const total = lines.reduce((s, l) => s + l.amount, 0);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="drill-modal">
      <div className="bg-white max-w-3xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div>
            <div className="rn3-eyebrow">{lang === "es" ? "Detalle de Cuenta" : "Account Detail"}</div>
            <h3 className="font-serif-display text-2xl mt-1">{account?.code} — {lang === "es" && account?.name_es ? account.name_es : account?.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          <table className="w-full text-sm" data-testid="drill-table">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
              <tr>
                <th className="py-2 px-3 w-28">{lang === "es" ? "Fecha" : "Date"}</th>
                <th className="py-2 px-3">{lang === "es" ? "Descripción" : "Description"}</th>
                <th className="py-2 px-3 text-right w-32">{lang === "es" ? "Monto" : "Amount"}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-[#F3EFE9]">
                  <td className="py-2 px-3 whitespace-nowrap">{l.date}</td>
                  <td className="py-2 px-3">{l.description}</td>
                  <td className={`py-2 px-3 text-right tabular-nums ${l.amount < 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(l.amount)}</td>
                </tr>
              ))}
              <tr className="bg-[#FDFBF7] font-semibold">
                <td colSpan="2" className="py-2 px-3 text-right">{lang === "es" ? "Total" : "Total"}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmt(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ---------- P&L ---------- */
const PLView = ({ data, lang, onDrill }) => {
  if (!data) return null;
  const sections = data.sections || {};
  const totals = data.totals || {};
  const sec = (key, label) => {
    const rows = sections[key] || [];
    if (rows.length === 0) return null;
    const total = key === "income" ? totals.income : (key === "cogs" ? totals.cogs : totals.expense);
    return (
      <div key={key} className="mb-8">
        <h3 className="font-serif-display text-xl">{label}</h3>
        <div className="border-t border-[#C4A252] w-8 mt-2 mb-3" />
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7] cursor-pointer" onClick={() => onDrill(r.code)} data-testid={`pl-row-${r.code}`}>
                <td className="py-2 px-3 w-20 text-[#737373] tabular-nums">{r.code}</td>
                <td className="py-2 px-3">{lang === "es" && r.name_es ? r.name_es : r.name}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmt(r.amount)}</td>
              </tr>
            ))}
            <tr className="bg-[#FDFBF7] font-semibold">
              <td className="py-2 px-3"></td>
              <td className="py-2 px-3">{lang === "es" ? "Total" : "Total"} {label}</td>
              <td className="py-2 px-3 text-right tabular-nums">{fmt(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <div data-testid="pl-view">
      {sec("income", lang === "es" ? "Ingresos" : "Income")}
      {sec("cogs", lang === "es" ? "Costo de Bienes Vendidos" : "Cost of Goods Sold")}
      <div className="mb-6 border-y-2 border-[#C4A252] py-3 flex items-center justify-between font-serif-display">
        <span className="text-lg">{lang === "es" ? "Utilidad Bruta" : "Gross Profit"}</span>
        <span className="text-2xl">{fmt(totals.gross_profit)}</span>
      </div>
      {sec("expense", lang === "es" ? "Gastos" : "Expenses")}
      <div className="border-y-2 border-[#8B1C22] py-4 flex items-center justify-between font-serif-display">
        <span className="text-xl">{lang === "es" ? "Utilidad Neta" : "Net Income"}</span>
        <span className={`text-3xl ${(totals.net_income || 0) >= 0 ? "text-[#0A0A0A]" : "text-[#8B1C22]"}`}>{fmt(totals.net_income)}</span>
      </div>
    </div>
  );
};

/* ---------- Balance Sheet ---------- */
const BSView = ({ data, lang, onDrill }) => {
  if (!data) return null;
  const sections = data.sections || {};
  const totals = data.totals || {};
  const sec = (key, label, totalKey) => {
    const rows = sections[key] || [];
    if (rows.length === 0) return null;
    return (
      <div key={key} className="mb-8">
        <h3 className="font-serif-display text-xl">{label}</h3>
        <div className="border-t border-[#C4A252] w-8 mt-2 mb-3" />
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}
                  className={`border-b border-[#F3EFE9] ${!r.is_computed ? "hover:bg-[#FDFBF7] cursor-pointer" : "italic text-[#737373]"}`}
                  onClick={() => !r.is_computed && onDrill(r.code)}
                  data-testid={`bs-row-${r.code}`}
              >
                <td className="py-2 px-3 w-20 text-[#737373] tabular-nums">{r.code}</td>
                <td className="py-2 px-3">{lang === "es" && r.name_es ? r.name_es : r.name}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmt(r.amount)}</td>
              </tr>
            ))}
            <tr className="bg-[#FDFBF7] font-semibold">
              <td className="py-2 px-3"></td>
              <td className="py-2 px-3">{lang === "es" ? "Total" : "Total"} {label}</td>
              <td className="py-2 px-3 text-right tabular-nums">{fmt(totals[totalKey])}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <div data-testid="bs-view">
      {sec("asset", lang === "es" ? "Activos" : "Assets", "assets")}
      <div className="mb-6 border-y-2 border-[#C4A252] py-3 flex items-center justify-between font-serif-display">
        <span className="text-lg">{lang === "es" ? "Total Activos" : "Total Assets"}</span>
        <span className="text-2xl">{fmt(totals.assets)}</span>
      </div>
      {sec("liability", lang === "es" ? "Pasivos" : "Liabilities", "liabilities")}
      {sec("equity", lang === "es" ? "Capital" : "Equity", "equity")}
      <div className="border-y-2 border-[#8B1C22] py-4 flex items-center justify-between font-serif-display">
        <span className="text-xl">{lang === "es" ? "Total Pasivos + Capital" : "Total Liabilities + Equity"}</span>
        <span className="text-3xl">{fmt(totals.liabilities_plus_equity)}</span>
      </div>
      {Math.abs(totals.out_of_balance || 0) > 0.005 && (
        <div className="mt-3 text-xs text-[#8B1C22]" data-testid="bs-oob">
          {lang === "es" ? "Diferencia" : "Out of balance"}: {fmt(totals.out_of_balance)}
        </div>
      )}
    </div>
  );
};

/* ---------- Cash Flow ---------- */
const CFView = ({ data, lang, onDrill }) => {
  if (!data) return null;
  // Defensive defaults so a partial / errored payload can't crash the view.
  const inflows = Array.isArray(data.inflows) ? data.inflows : [];
  const outflows = Array.isArray(data.outflows) ? data.outflows : [];
  const totals = data.totals || {};
  const lineGroup = (rows, totalLabel, totalValue) => (
    <table className="w-full text-sm mb-6">
      <tbody>
        {(rows || []).map((r) => (
          <tr key={r.code} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7] cursor-pointer" onClick={() => onDrill(r.code)} data-testid={`cf-row-${r.code}`}>
            <td className="py-2 px-3 w-20 text-[#737373] tabular-nums">{r.code}</td>
            <td className="py-2 px-3">{lang === "es" && r.name_es ? r.name_es : r.name}</td>
            <td className="py-2 px-3 text-right tabular-nums">{fmt(r.amount)}</td>
          </tr>
        ))}
        <tr className="bg-[#FDFBF7] font-semibold">
          <td className="py-2 px-3"></td>
          <td className="py-2 px-3">{totalLabel}</td>
          <td className="py-2 px-3 text-right tabular-nums">{fmt(totalValue)}</td>
        </tr>
      </tbody>
    </table>
  );
  return (
    <div data-testid="cf-view">
      <div className="border border-[#E5E5E5] p-4 mb-6 flex items-center justify-between font-serif-display">
        <span>{lang === "es" ? "Efectivo Inicial" : "Beginning Cash"}</span>
        <span className="text-2xl">{fmt(totals.beginning_cash)}</span>
      </div>
      <h3 className="font-serif-display text-xl">{lang === "es" ? "Entradas" : "Operating Inflows"}</h3>
      <div className="border-t border-[#C4A252] w-8 mt-2 mb-3" />
      {lineGroup(inflows, (lang === "es" ? "Total Entradas" : "Total Inflows"), totals.total_inflow)}
      <h3 className="font-serif-display text-xl">{lang === "es" ? "Salidas" : "Operating Outflows"}</h3>
      <div className="border-t border-[#C4A252] w-8 mt-2 mb-3" />
      {lineGroup(outflows, (lang === "es" ? "Total Salidas" : "Total Outflows"), totals.total_outflow)}
      <div className="border-y-2 border-[#C4A252] py-3 mb-3 flex items-center justify-between font-serif-display">
        <span className="text-lg">{lang === "es" ? "Cambio Neto en Efectivo" : "Net Change in Cash"}</span>
        <span className={`text-2xl ${(totals.net_change || 0) >= 0 ? "text-[#15803D]" : "text-[#8B1C22]"}`}>{fmt(totals.net_change)}</span>
      </div>
      <div className="border-y-2 border-[#8B1C22] py-4 flex items-center justify-between font-serif-display">
        <span className="text-xl">{lang === "es" ? "Efectivo Final" : "Ending Cash"}</span>
        <span className="text-3xl">{fmt(totals.ending_cash)}</span>
      </div>
    </div>
  );
};

/* ---------- General Ledger ---------- */
const GLView = ({ data, lang }) => {
  const [open, setOpen] = useState({});
  if (!data) return null;
  const accounts = Array.isArray(data.accounts) ? data.accounts : [];
  return (
    <div data-testid="gl-view" className="space-y-2">
      {accounts.length === 0 && <div className="text-sm text-[#737373]">{lang === "es" ? "Sin transacciones." : "No transactions."}</div>}
      {accounts.map((a) => {
        const lines = Array.isArray(a.lines) ? a.lines : [];
        return (
        <div key={a.code} className="border border-[#E5E5E5]">
          <button
            onClick={() => setOpen({ ...open, [a.code]: !open[a.code] })}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#FDFBF7] hover:bg-[#F3EFE9] text-left"
            data-testid={`gl-row-${a.code}`}
          >
            <div className="flex items-center gap-3">
              {open[a.code] ? <ChevronDown className="w-4 h-4 text-[#737373]" /> : <ChevronRight className="w-4 h-4 text-[#737373]" />}
              <span className="text-[#737373] tabular-nums text-sm w-16">{a.code}</span>
              <span className="font-medium">{lang === "es" && a.name_es ? a.name_es : a.name}</span>
              {a.nickname && <span className="text-xs italic text-[#8B6914]">"{a.nickname}"</span>}
              <span className="text-xs uppercase tracking-widest text-[#C4A252]">{a.detail_type || a.type}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-[#737373]">{lines.length} lines</span>
              <span className="font-serif-display text-lg tabular-nums">{fmt(a.total)}</span>
            </div>
          </button>
          {open[a.code] && (
            <table className="w-full text-sm border-t border-[#E5E5E5]">
              <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
                <tr><th className="py-2 px-3 w-28">{lang === "es" ? "Fecha" : "Date"}</th><th className="py-2 px-3">{lang === "es" ? "Descripción" : "Description"}</th><th className="py-2 px-3 text-right w-32">{lang === "es" ? "Monto" : "Amount"}</th></tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-[#F3EFE9]">
                    <td className="py-1.5 px-3 whitespace-nowrap">{l.date}</td>
                    <td className="py-1.5 px-3">{l.description}</td>
                    <td className={`py-1.5 px-3 text-right tabular-nums ${l.amount < 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        );
      })}
    </div>
  );
};

/* ---------- Top-level ReportsTab ---------- */
const ReportsTab = ({ book, accounts, lang }) => {
  const [report, setReport] = useState("pl");
  const [preset, setPreset] = useState("ytd");
  const [range, setRange] = useState(presetRange("ytd"));
  const [asOf, setAsOf] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // { code, lines }
  const [dataHint, setDataHint] = useState(null); // { min, max, count } — earliest/latest txn dates for helpful banner

  const accountByCode = useMemo(() => {
    const m = {}; for (const a of accounts) m[a.code] = a; return m;
  }, [accounts]);

  // On book change: fetch min/max txn date. If the default YTD window has no
  // data but there are historical txns, auto-shift the range so the user sees
  // real numbers instead of a bunch of $0.00.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: dr } = await api.get(`/back-office/books/${book.id}/reports/date-range`);
        if (cancelled) return;
        setDataHint(dr);
        if (dr?.min && dr?.max) {
          const ytd = presetRange("ytd");
          const overlaps = dr.max >= ytd.start && dr.min <= ytd.end;
          if (!overlaps) {
            setRange({ start: dr.min, end: dr.max });
            setPreset("custom");
            setAsOf(dr.max);
          }
        }
      } catch { /* silent — falls back to YTD */ }
    })();
    return () => { cancelled = true; };
  }, [book.id]);

  const load = async () => {
    setLoading(true);
    setData(null); // clear stale data from a different report shape
    try {
      let path = "";
      let qs = "";
      if (report === "pl") { path = "profit-loss"; qs = `start=${range.start}&end=${range.end}`; }
      else if (report === "bs") { path = "balance-sheet"; qs = `as_of=${asOf}`; }
      else if (report === "cf") { path = "cash-flow"; qs = `start=${range.start}&end=${range.end}`; }
      else if (report === "gl") { path = "general-ledger"; qs = `start=${range.start}&end=${range.end}`; }
      const { data } = await api.get(`/back-office/books/${book.id}/reports/${path}?${qs}`);
      setData(data);
    } catch (err) {
      toast.error("Failed to load report");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id, report, range.start, range.end, asOf]);

  const onPreset = (key) => {
    setPreset(key);
    if (key !== "custom") setRange(presetRange(key));
  };

  // drill
  const openDrill = async (code) => {
    try {
      const { data } = await api.get(
        `/back-office/books/${book.id}/reports/general-ledger?start=${range.start}&end=${range.end}&account=${code}`
      );
      const acc = data.accounts[0];
      setDrill({ account: accountByCode[code] || { code }, lines: acc?.lines || [] });
    } catch { toast.error("Drill failed"); }
  };

  // export helpers (download via blob to include auth header)
  const download = async (path, suggestedName) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = suggestedName; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Export failed"); }
  };

  const tag = REPORTS.find((r) => r.key === report);
  const title = lang === "es" ? tag.es : tag.en;

  return (
    <div className="space-y-6" data-testid="reports-tab">
      {/* Report picker tabs */}
      <div className="flex flex-wrap gap-2" data-testid="report-picker">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <button
              key={r.key}
              onClick={() => setReport(r.key)}
              data-testid={`pick-${r.key}`}
              className={`px-4 py-2 text-sm border inline-flex items-center gap-2 ${report === r.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#C4A252]"}`}
            >
              <Icon className="w-4 h-4" />
              {lang === "es" ? r.es : r.en}
            </button>
          );
        })}
      </div>

      {/* Date controls */}
      <div className="flex flex-wrap items-end gap-3 border-b border-[#E5E5E5] pb-4">
        {report === "bs" ? (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">{lang === "es" ? "Al" : "As of"}</label>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="rn3-input text-sm" data-testid="report-asof" />
          </div>
        ) : (
          <>
            <div className="flex gap-1" data-testid="report-presets">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => onPreset(p.key)}
                  data-testid={`preset-${p.key}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border ${preset === p.key ? "bg-[#8B1C22] text-white border-[#8B1C22]" : "border-[#E5E5E5] hover:border-[#C4A252]"}`}
                >{lang === "es" ? p.es : p.en}</button>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#737373] block">{lang === "es" ? "Inicio" : "Start"}</label>
              <input type="date" value={range.start} onChange={(e) => { setPreset("custom"); setRange({ ...range, start: e.target.value }); }} className="rn3-input text-sm" data-testid="report-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#737373] block">{lang === "es" ? "Fin" : "End"}</label>
              <input type="date" value={range.end} onChange={(e) => { setPreset("custom"); setRange({ ...range, end: e.target.value }); }} className="rn3-input text-sm" data-testid="report-end" />
            </div>
          </>
        )}

        <div className="ml-auto flex flex-wrap gap-2" data-testid="report-exports">
          {report !== "gl" && (
            <button onClick={() => {
              const path = report === "bs"
                ? `/back-office/books/${book.id}/reports/balance-sheet.csv?as_of=${asOf}&lang=${lang}`
                : `/back-office/books/${book.id}/reports/${report === "pl" ? "profit-loss" : "cash-flow"}.csv?start=${range.start}&end=${range.end}&lang=${lang}`;
              download(path, `${report}_${book.name}_${lang}.csv`);
            }} className="rn3-btn-gold text-xs" data-testid="export-csv">
              <FileDown className="w-3.5 h-3.5" /> CSV
            </button>
          )}
          <button onClick={() => download(
            `/back-office/books/${book.id}/reports/financials.xlsx?start=${range.start}&end=${range.end}&as_of=${report === "bs" ? asOf : range.end}&lang=${lang}`,
            `Financials_${book.name}_${lang}.xlsx`
          )} className="rn3-btn-gold text-xs" data-testid="export-xlsx">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </button>
          <button onClick={() => download(
            `/back-office/books/${book.id}/reports/qbo.json?start=${range.start}&end=${range.end}`,
            `QBO_${book.name}.json`
          )} className="rn3-btn-gold text-xs" data-testid="export-qbo">
            <FileJson className="w-3.5 h-3.5" /> QBO
          </button>
        </div>
      </div>

      {/* Heading */}
      <div className="flex items-end justify-between flex-wrap">
        <div>
          <h2 className="font-serif-display text-3xl">{title}</h2>
          <div className="text-xs text-[#737373] mt-1">
            {book.name}
            {report === "bs" ? ` · ${lang === "es" ? "Al" : "As of"} ${asOf}` : ` · ${range.start} → ${range.end}`}
          </div>
          {dataHint && dataHint.count > 0 && (
            <button
              type="button"
              onClick={() => {
                setRange({ start: dataHint.min, end: dataHint.max });
                setAsOf(dataHint.max);
                setPreset("custom");
              }}
              className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#8B1C22] hover:underline"
              data-testid="fit-to-data-btn"
              title={lang === "es" ? "Ajustar al rango de datos" : "Fit range to transactions"}
            >
              <Calendar className="w-3 h-3" />
              {lang === "es"
                ? `${dataHint.count} txns · ${dataHint.min} → ${dataHint.max} · Ajustar rango`
                : `${dataHint.count} txns on file · ${dataHint.min} → ${dataHint.max} · Fit range`}
            </button>
          )}
          {dataHint && dataHint.count === 0 && (
            <div className="mt-2 text-[11px] uppercase tracking-widest text-[#C4A252]" data-testid="no-txns-hint">
              {lang === "es" ? "Este libro aún no tiene transacciones." : "This book has no transactions yet."}
            </div>
          )}
        </div>
      </div>

      {/* Report body */}
      {loading ? (
        <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : (
        <div className="border border-[#E5E5E5] p-6 bg-white">
          {report === "pl" && <PLView data={data} lang={lang} onDrill={openDrill} />}
          {report === "bs" && <BSView data={data} lang={lang} onDrill={openDrill} />}
          {report === "cf" && <CFView data={data} lang={lang} onDrill={openDrill} />}
          {report === "gl" && <GLView data={data} lang={lang} />}
        </div>
      )}

      <DrillModal open={!!drill} onClose={() => setDrill(null)} account={drill?.account} lines={drill?.lines || []} lang={lang} />
    </div>
  );
};

export default ReportsTab;
